interface FinancialSummaryCardProps {
  budget: number;
  spent: number;
  // New fields from GET /api/jobs/:id/financials
  revisedContractValue?: number;
  approvedChangeOrderTotal?: number;
  approvedChangeOrderCount?: number;
  remaining?: number;
  percentageUsed?: number;
  profitMargin?: number;
  expensesTotal?: number;
  labourCost?: number;
  labourHours?: number;
  labourByWorker?: { name: string; hours: number; rate: number; cost: number }[];
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);

export const FinancialSummaryCard = ({
  budget,
  spent,
  revisedContractValue,
  approvedChangeOrderTotal,
  approvedChangeOrderCount,
  remaining: remainingProp,
  percentageUsed: percentageUsedProp,
  profitMargin,
  expensesTotal,
  labourCost,
  labourHours,
  labourByWorker,
}: FinancialSummaryCardProps) => {
  // Fall back to local calculation if server values not yet available
  const effectiveRevised = revisedContractValue ?? budget;
  const remaining = remainingProp ?? effectiveRevised - spent;
  const percentage = percentageUsedProp != null
    ? Math.round(percentageUsedProp)
    : effectiveRevised > 0 ? Math.round((spent / effectiveRevised) * 100) : 0;

  const hasChangeOrders =
    approvedChangeOrderTotal != null &&
    approvedChangeOrderTotal !== 0;

  return (
    <div className="bg-white p-3.5 rounded-xl border border-gray-200">
      <h3 className="text-sm font-bold text-gray-900 mb-2">Financial Summary</h3>
      <div className="space-y-2.5">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Original Budget</span>
            <span className="font-medium text-gray-800">{formatCurrency(budget)}</span>
          </div>

          {hasChangeOrders && (
            <>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">
                  Approved Change Orders
                  {approvedChangeOrderCount != null && approvedChangeOrderCount > 0 && (
                    <span className="ml-1 text-xs text-gray-400">({approvedChangeOrderCount})</span>
                  )}
                </span>
                <span className="font-medium text-green-700">
                  +{formatCurrency(approvedChangeOrderTotal ?? 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm mb-1 border-t border-dashed border-gray-100 pt-1">
                <span className="font-semibold text-gray-700">Revised Contract Value</span>
                <span className="font-bold text-gray-900">{formatCurrency(effectiveRevised)}</span>
              </div>
            </>
          )}

          <div className="flex justify-between text-sm mb-1 mt-2">
            <span className="text-gray-600">Actual Cost to Date</span>
            <span className="font-medium text-gray-800">{formatCurrency(spent)}</span>
          </div>
          {expensesTotal != null && expensesTotal > 0 && (
            <div className="flex justify-between text-xs mb-1 text-gray-500">
              <span className="pl-3">↳ incl. scanned receipts</span>
              <span>{formatCurrency(expensesTotal)}</span>
            </div>
          )}
          {labourCost != null && labourCost > 0 && (
            <details className="mb-1">
              <summary className="flex justify-between text-xs text-gray-500 cursor-pointer list-none">
                <span className="pl-3">↳ incl. labour ({(labourHours ?? 0).toFixed(1)}h approved) ▾</span>
                <span>{formatCurrency(labourCost)}</span>
              </summary>
              {/* Each employee as its own line item */}
              {(labourByWorker || []).map(w => (
                <div key={w.name} className="flex justify-between text-[11px] text-gray-400 pl-6">
                  <span>{w.name} — {w.hours.toFixed(1)}h{w.rate > 0 ? ` @ $${w.rate.toFixed(2)}` : ' (no rate set)'}</span>
                  <span>{formatCurrency(w.cost)}</span>
                </div>
              ))}
            </details>
          )}
          <div className="flex justify-between text-sm font-bold">
            <span className="text-gray-600">Remaining Budget</span>
            <span className={remaining >= 0 ? 'text-green-600' : 'text-red-600'}>
              {formatCurrency(remaining)}
            </span>
          </div>

          {profitMargin != null && (
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-600">Profit Margin</span>
              <span className={profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}>
                {Math.round(profitMargin)}%
              </span>
            </div>
          )}
        </div>

        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className={`h-4 rounded-full transition-all ${
              percentage > 100 ? 'bg-red-500' : percentage > 90 ? 'bg-yellow-500' : 'bg-primary'
            }`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        <p className="text-center text-sm text-gray-600">
          {percentage}% of {hasChangeOrders ? 'revised contract' : 'budget'} used.
        </p>
      </div>
    </div>
  );
};
